import { useCallback } from "react"
import { Paging } from "../../models/paging/paging"
import { KeyedMutator } from "swr"
import useSWRInfinite from "swr/infinite"

type InfinityLoad<I, E> = {
  list: I[] | undefined
  error: E | undefined
  loadMore: () => void
  canLoadMore: boolean
  isLoading: boolean
  mutate: KeyedMutator<Paging<I>[]>
  resetIndex: () => Promise<void>
}

type Params<T> = {
  fetcher: (pageIndex: number) => Promise<Paging<T>>
  fetcherKey: string
  displayCount: number
  maxLength?: number
  revalidateAll?: boolean
}

export const useInfinityLoad = <T, E = never>({
  fetcher,
  fetcherKey,
  displayCount,
  maxLength,
  revalidateAll = false,
}: Params<T>): InfinityLoad<T, E> => {
  const getKey = (
    pageIndex: number,
    previousData: Paging<T> | null,
  ): [number, string] | null => {
    if (previousData === null) {
      return [pageIndex, fetcherKey]
    }

    const max =
      maxLength !== undefined
        ? Math.min(previousData.total, maxLength)
        : previousData.total

    return pageIndex * displayCount < max ? [pageIndex, fetcherKey] : null
  }

  const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<
    Paging<T>,
    E
  >(getKey, fetcher, { revalidateFirstPage: true, revalidateAll })

  const loadMore = useCallback(async () => {
    await setSize(size + 1)
  }, [setSize, size])

  const list =
    data !== undefined
      ? data.reduce((output, result) => {
          return output.concat(result.items)
        }, [] as T[])
      : undefined

  const canLoadMore = (() => {
    if (data === undefined || data[0] === undefined) {
      return false
    }

    const max =
      maxLength !== undefined
        ? Math.min(data[0].total, maxLength)
        : data[0].total

    return (list as T[]).length < max
  })()

  const resetIndex = async () => {
    await setSize(0)
  }

  return {
    list,
    error,
    loadMore,
    canLoadMore,
    isLoading: isValidating,
    mutate,
    resetIndex,
  }
}
